import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import { WorkflowsContext } from "@/context/WorkflowsContext";
import AuthService from "@/services/AuthService";

interface Deployment {
  id: number;
  deploymentId: string;
  displayName: string;
  teamId: number;
}

function buildPreviewUrl(deployment: Deployment, workflowCount: number): string {
  const src = encodeURIComponent(
    `${import.meta.env.VITE_MESSAGING_CLIENT_URL as string}/messenger/embed.js`
  );
  const name = encodeURIComponent(deployment.displayName || deployment.deploymentId);
  const wf = workflowCount > 0 ? 1 : 0;
  const ht = deployment.teamId ? 1 : 0;
  return (
    `${window.location.origin}/embed-preview.html` +
    `?siteId=${AuthService.profile.siteId}` +
    `&deploymentId=${deployment.deploymentId}` +
    `&name=${name}` +
    `&src=${src}` +
    `&wf=${wf}` +
    `&ht=${ht}`
  );
}

export default function ChatPreview() {
  const api = useApi();
  const { workflows } = useContext(WorkflowsContext);
  const activeWorkflows = workflows.filter(
    (w) => w.enabled && w.workflowStatus !== "deprecated"
  );
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [selected, setSelected] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.messaging
      .get("Deployment")
      .then((r) => r.json())
      .then((data) => {
        const list: Deployment[] = Array.isArray(data) ? data : [];
        setDeployments(list);
        if (list.length > 0) setSelected(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <LoadingSpinner />;

  const previewUrl = selected ? buildPreviewUrl(selected, activeWorkflows.length) : "";

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-800">Preview Chat</h1>
        <p className="text-sm text-slate-500 mt-1">
          Test your live chat widget exactly as visitors see it — workflows, bot
          responses, and all channels included.
        </p>
      </div>

      {deployments.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <p className="text-slate-600 mb-3">No deployments configured yet.</p>
          <Link to="/deployment" className="text-blue-600 hover:underline text-sm">
            Set up a deployment →
          </Link>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-6 space-y-5">
          {/* Deployment picker — only shown when more than one exists */}
          {deployments.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Deployment
              </label>
              <select
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selected?.id ?? ""}
                onChange={(e) => {
                  const found = deployments.find(
                    (d) => d.id === Number(e.target.value)
                  );
                  setSelected(found ?? null);
                }}
              >
                {deployments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.displayName || d.deploymentId}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selected && (
            <>
              {/* Status strip */}
              <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-3 space-y-1">
                <div className="text-sm font-medium text-slate-800">
                  {selected.displayName || selected.deploymentId}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                  {selected.teamId ? (
                    <span className="text-emerald-600">✓ Team assigned</span>
                  ) : (
                    <span className="text-amber-600">
                      ⚠ No team — visitors may queue indefinitely
                    </span>
                  )}
                  {activeWorkflows.length > 0 ? (
                    <span className="text-emerald-600">
                      ✓ {activeWorkflows.length} workflow{activeWorkflows.length !== 1 ? "s" : ""} active
                    </span>
                  ) : (
                    <span className="text-amber-600">
                      ⚠ No workflows —{" "}
                      <Link to="/workflows" className="underline">
                        create one
                      </Link>{" "}
                      to test bot responses
                    </span>
                  )}
                </div>
              </div>

              {/* Launch button */}
              <button
                onClick={() =>
                  window.open(previewUrl, `velaro-preview-${selected.id}`)
                }
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Launch Preview
              </button>

              <p className="text-xs text-slate-400 text-center">
                Opens in a new tab with a mock website background. Use the
                sidebar to swap backgrounds or reset the chat session.
              </p>
            </>
          )}
        </div>
      )}

      <div className="mt-4 text-xs text-slate-400 text-center">
        Deployment settings →{" "}
        <Link to="/deployment" className="underline">
          Chat Embed
        </Link>
      </div>
    </div>
  );
}
